/**
 * CheatMenuTabs - New tabbed cheat menu interface
 * Comprehensive tabbed interface for cheating in the game
 */

import type { Component, ChangeEvent, ReactNode } from 'react';
import { Variable } from '../models/Variable';
import { CustomProperty } from '../models/CustomProperty';
import { Timer } from '../models/Timer';
import { Room } from '../models/Room';
import { RagsObject } from '../models/Object';
import { Character } from '../models/Character';

// React is available globally from CDN
const React = (window as any).React;
const ReactComponent = React.Component;

// Forward declarations
declare const TheGame: any;
declare const cheatFreezes: {
	variables: { [key: string]: boolean };
	playerProperties: { [key: string]: boolean };
	customProperties?: { [entityId: string]: { [propertyName: string]: boolean } };
};
declare const Finder: any;
declare const GameTimers: any;
declare const GameActions: any;
declare const ChangeRoom: any;
declare const GameCommands: any;
declare const SetVariable: any;

type CheatMenuTabKey = 'variables' | 'timers' | 'rooms' | 'objects' | 'characters' | 'player';
type SortDirection = 'asc' | 'desc';
type SortState = { key: string; direction: SortDirection };

interface CheatMenuTabsState {
	activeTab: CheatMenuTabKey;
	filters: { [key: string]: string };
	pages: { [key: string]: number };
	sorts: { [key: string]: SortState };
	refreshKey: number;
	objectLocationFilter: 'all' | 'playerInventory' | 'currentRoom' | string; // 'all', 'playerInventory', 'currentRoom', room ID, or character name
	characterLocationFilter: 'all' | 'currentRoom' | string; // 'all', 'currentRoom', or room ID
	roomFilter: 'all' | 'currentRoom' | 'surroundingRooms'; // 'all', 'currentRoom', 'surroundingRooms'
}

class CheatMenuTabs extends ReactComponent<{}, CheatMenuTabsState> {
	constructor(props: {}) {
		super(props);
		const savedState = (window as any).cheatMenuUiState || {};
		this.state = {
			activeTab: savedState.activeTab || 'variables',
			filters: savedState.filters || {},
			pages: savedState.pages || {},
			sorts: savedState.sorts || {},
			refreshKey: 0,
			objectLocationFilter: savedState.objectLocationFilter || 'all',
			characterLocationFilter: savedState.characterLocationFilter || 'all',
			roomFilter: savedState.roomFilter || 'all',
		};
	}

	componentDidUpdate(): void {
		(window as any).cheatMenuUiState = {
			activeTab: this.state.activeTab,
			filters: this.state.filters,
			pages: this.state.pages,
			sorts: this.state.sorts,
			objectLocationFilter: this.state.objectLocationFilter,
			characterLocationFilter: this.state.characterLocationFilter,
			roomFilter: this.state.roomFilter,
		};
	}

	setActiveTab = (tab: CheatMenuTabKey): void => {
		this.setState({
			activeTab: tab,
		});
	};

	getFilter = (tab: CheatMenuTabKey): string => {
		return this.state.filters[tab] || '';
	};

	setFilter = (tab: CheatMenuTabKey, value: string): void => {
		this.setState({
			filters: {
				...this.state.filters,
				[tab]: value,
			},
		});
	};

	getPage = (tab: CheatMenuTabKey): number => {
		return this.state.pages[tab] || 1;
	};

	setPage = (tab: CheatMenuTabKey, page: number): void => {
		this.setState({
			pages: {
				...this.state.pages,
				[tab]: page,
			},
		});
	};

	handleFilterChange = (tab: CheatMenuTabKey) => (e: ChangeEvent<HTMLInputElement>): void => {
		this.setFilter(tab, e.target.value);
	};

	getSort = (tab: CheatMenuTabKey): SortState => {
		const defaults: { [key in CheatMenuTabKey]: SortState } = {
			variables: { key: 'name', direction: 'asc' },
			timers: { key: 'order', direction: 'asc' },
			rooms: { key: 'name', direction: 'asc' },
			objects: { key: 'name', direction: 'asc' },
			characters: { key: 'name', direction: 'asc' },
			player: { key: 'name', direction: 'asc' },
		};
		return this.state.sorts[tab] || defaults[tab];
	};

	toggleSort = (tab: CheatMenuTabKey, key: string): void => {
		const current = this.getSort(tab);
		const direction: SortDirection = current.key === key && current.direction === 'asc' ? 'desc' : 'asc';
		this.setState({
			sorts: {
				...this.state.sorts,
				[tab]: { key, direction },
			},
			pages: {
				...this.state.pages,
				[tab]: 1,
			},
		});
	};

	renderSortableHeader(tab: CheatMenuTabKey, key: string, label: string, align?: 'left' | 'center' | 'right'): ReactNode {
		const current = this.getSort(tab);
		const isActive = current.key === key;
		const icon = isActive ? (current.direction === 'asc' ? ' ▲' : ' ▼') : '';
		return React.createElement('th', {
			onClick: () => this.toggleSort(tab, key),
			style: {
				cursor: 'pointer',
				textAlign: align || 'left',
				userSelect: 'none',
			},
			title: 'Click to sort',
		}, `${label}${icon}`);
	}

	render(): ReactNode {
		if (!TheGame) return null;

		const tabs: CheatMenuTabKey[] = ['variables', 'timers', 'rooms', 'objects', 'characters', 'player'];

		return React.createElement('div', {
				className: 'cheat-menu-tabs',
				style: { display: 'flex', flexDirection: 'column', height: '100%' },
			},
			// Tab buttons
			React.createElement('div', {
					className: 'cheat-tab-buttons',
					style: {
						display: 'flex',
						borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
						flex: '0 0 auto',
					},
				},
				tabs.map(tab =>
					React.createElement('button', {
						key: tab,
						className: `cheat-tab-button ${this.state.activeTab === tab ? 'active' : ''}`,
						onClick: () => this.setActiveTab(tab),
						style: {
							padding: '12px 20px',
							border: 'none',
							background: this.state.activeTab === tab ? 'var(--main-bg-color)' : 'transparent',
							borderBottom: this.state.activeTab === tab ? '2px solid var(--main-text-color)' : '2px solid transparent',
							cursor: 'pointer',
							textTransform: 'capitalize',
							fontFamily: 'var(--font-game-text)',
							fontSize: '14px',
							fontWeight: this.state.activeTab === tab ? '600' : '400',
						},
					}, tab),
				),
			),
			// Tab content (with padding)
			React.createElement('div', {
					className: 'cheat-tab-content',
					style: {
						flex: '1 1 auto',
						overflow: 'hidden',
						display: 'flex',
						flexDirection: 'column',
						padding: '20px',
					},
				},
				this.renderTabContent(),
			),
		);
	}

	renderTabContent(): ReactNode {
		switch (this.state.activeTab) {
			case 'variables':
				return this.renderVariablesTab();
			case 'timers':
				return this.renderTimersTab();
			case 'rooms':
				return this.renderRoomsTab();
			case 'objects':
				return this.renderObjectsTab();
			case 'characters':
				return this.renderCharactersTab();
			case 'player':
				return this.renderPlayerTab();
			default:
				return null;
		}
	}

	// Helper to get variable display value
	getVariableValue(variable: Variable): string {
		if (variable.vartype === 'VT_NUMBER') {
			return String(variable.dNumType);
		} else if (variable.vartype === 'VT_STRING') {
			return variable.sString;
		} else if (variable.vartype === 'VT_DATETIME') {
			return variable.dtDateTime || '';
		} else if (variable.vartype === 'VT_NUMBERARRAY' && variable.VarArray) {
			return JSON.stringify(variable.VarArray);
		} else if (variable.vartype === 'VT_STRINGARRAY' && variable.VarArray) {
			return JSON.stringify(variable.VarArray);
		} else if (variable.vartype === 'VT_DATETIMEARRAY' && variable.VarArray) {
			return JSON.stringify(variable.VarArray);
		}
		return '';
	}

	// Helper to set variable value
	setVariableValue(variable: Variable, value: string): void {
		if (variable.vartype === 'VT_NUMBER') {
			const num = parseFloat(value);
			if (!isNaN(num)) {
				variable.dNumType = num;
			}
		} else if (variable.vartype === 'VT_STRING') {
			variable.sString = value;
		} else if (variable.vartype === 'VT_DATETIME') {
			variable.dtDateTime = value;
		} else if (variable.vartype === 'VT_NUMBERARRAY') {
			try {
				const arr = JSON.parse(value);
				if (Array.isArray(arr)) {
					variable.VarArray = arr.map((v: any) => parseFloat(String(v)));
				}
			} catch (e) {
				console.error('Failed to parse number array:', e);
			}
		} else if (variable.vartype === 'VT_STRINGARRAY') {
			try {
				const arr = JSON.parse(value);
				if (Array.isArray(arr)) {
					variable.VarArray = arr.map((v: any) => String(v));
				}
			} catch (e) {
				console.error('Failed to parse string array:', e);
			}
		} else if (variable.vartype === 'VT_DATETIMEARRAY') {
			try {
				const arr = JSON.parse(value);
				if (Array.isArray(arr)) {
					variable.VarArray = arr.map((v: any) => String(v));
				}
			} catch (e) {
				console.error('Failed to parse datetime array:', e);
			}
		}
	}

	getVariableTypeLabel(variable: Variable): string {
		switch (variable.vartype) {
			case 'VT_NUMBER':
				return 'Number';
			case 'VT_STRING':
				return 'Text';
			case 'VT_DATETIME':
				return 'Date/Time';
			case 'VT_NUMBERARRAY':
				return 'Number Array';
			case 'VT_STRINGARRAY':
				return 'Text Array';
			case 'VT_DATETIMEARRAY':
				return 'Date/Time Array';
			default:
				return variable.vartype || 'Unknown';
		}
	}

	renderVariablesTab(): ReactNode {
		if (!TheGame || !TheGame.Variables) return null;

		const PaginatedTable = (window as any).PaginatedTable;
		const filter = this.getFilter('variables');
		const sort = this.getSort('variables');

		// Filter and sort variables
		let filteredVars = TheGame.Variables.filter((v: Variable) => {
			if (!filter) return true;
			const filterLower = filter.toLowerCase();
			return v.varname.toLowerCase().includes(filterLower) ||
				(v.VarComment && v.VarComment.toLowerCase().includes(filterLower));
		});

		const withIndex = filteredVars.map((variable: Variable, index: number) => ({ variable, index }));
		withIndex.sort((a: { variable: Variable; index: number }, b: { variable: Variable; index: number }) => {
			const direction = sort.direction === 'asc' ? 1 : -1;
			const aVar = a.variable;
			const bVar = b.variable;
			let result = 0;
			switch (sort.key) {
				case 'type':
					result = this.getVariableTypeLabel(aVar).toLowerCase().localeCompare(this.getVariableTypeLabel(bVar).toLowerCase());
					break;
				case 'value':
					result = this.getVariableValue(aVar).toLowerCase().localeCompare(this.getVariableValue(bVar).toLowerCase());
					break;
				case 'description':
					result = (aVar.VarComment || '').toLowerCase().localeCompare((bVar.VarComment || '').toLowerCase());
					break;
				case 'name':
				default:
					result = aVar.varname.toLowerCase().localeCompare(bVar.varname.toLowerCase());
					break;
			}
			if (result === 0) {
				result = a.index - b.index;
			}
			return result * direction;
		});
		filteredVars = withIndex.map((entry: { variable: Variable; index: number }) => entry.variable);

		const renderRow = (variable: Variable, index: number) => {
			const isFrozen = !!(cheatFreezes.variables[variable.varname]);
			const value = this.getVariableValue(variable);

			return React.createElement('tr', {
					key: variable.varname,
					style: { height: '70px' },
				},
				React.createElement('td', {
						style: {
							height: '70px',
							lineHeight: '1.33rem',
							overflow: 'hidden',
							textOverflow: 'ellipsis',
							whiteSpace: 'nowrap',
						},
					},
					React.createElement('span', null, variable.varname),
					isFrozen && React.createElement('span', {
						style: {
							marginLeft: '8px',
							color: '#ff6b6b',
							fontSize: '12px',
						},
						title: 'Frozen',
					}, '🔒'),
				),
				React.createElement('td', {
						style: {
							fontSize: '12px',
							height: '70px',
							lineHeight: '1.33rem',
						},
					},
					React.createElement('span', null, this.getVariableTypeLabel(variable)),
				),
				React.createElement('td', {
						style: {
							fontFamily: 'monospace',
							fontSize: '12px',
							maxWidth: '8rem',
							height: '70px',
							lineHeight: '1.33rem',
						},
					},
					React.createElement('span', {
						style: {
							display: '-webkit-box',
							WebkitLineClamp: 2,
							WebkitBoxOrient: 'vertical',
							overflow: 'hidden',
							height: '2.8rem',
							lineHeight: '1.33rem',
						},
					}, value),
				),
				React.createElement('td', {
						style: {
							maxWidth: '400px',
							fontSize: '12px',
							color: 'var(--main-text-color)',
							opacity: 0.8,
							height: '70px',
							lineHeight: '1.33rem',
						},
					},
					React.createElement('span', {
						style: {
							display: '-webkit-box',
							WebkitLineClamp: 2,
							WebkitBoxOrient: 'vertical',
							overflow: 'hidden',
							height: '2.8rem',
							lineHeight: '1.33rem',
						},
					}, variable.VarComment || ''),
				),
				React.createElement('td', {
						style: {
							height: '70px',
							lineHeight: '1.33rem',
						},
					},
					React.createElement('div', { style: { display: 'flex', gap: '6px' } },
						React.createElement('button', {
							className: 'btn',
							onClick: () => {
								this.openVariableEditDialog(variable);
							},
						}, 'Edit'),
						React.createElement('button', {
							className: 'btn',
							onClick: () => {
								this.openPropertiesDialog('variable', `variable:${variable.varname}`, variable.varname, variable.CustomProperties || []);
							},
						}, 'Properties'),
					),
				),
			);
		};

		const colgroup = React.createElement('colgroup', null,
			React.createElement('col', { style: { width: '200px' } }), // Name
			React.createElement('col', { style: { width: '140px' } }), // Type
			React.createElement('col', { style: { width: '8rem' } }), // Value
			React.createElement('col', { style: { width: '100%' } }), // Description
			React.createElement('col', { style: { width: '180px' } }), // Actions
		);

		const tableHeaders = React.createElement('thead', null,
			React.createElement('tr', null,
				this.renderSortableHeader('variables', 'name', 'Name'),
				this.renderSortableHeader('variables', 'type', 'Type'),
				this.renderSortableHeader('variables', 'value', 'Value'),
				this.renderSortableHeader('variables', 'description', 'Description'),
				React.createElement('th', null, ''), // Empty header for actions column
			),
		);

		return React.createElement('div', {
				style: {
					display: 'flex',
					flexDirection: 'column',
					height: '100%',
					gap: '16px',
				},
			},
			// Filter input
			React.createElement('input', {
				type: 'text',
				placeholder: 'Filter variables...',
				value: filter,
				onChange: this.handleFilterChange('variables'),
				style: {
					width: '100%',
					padding: '8px 12px',
					borderRadius: '4px',
					fontFamily: 'var(--font-game-text)',
					fontSize: '14px',
					background: 'var(--main-bg-color)',
					color: 'var(--main-text-color)',
					outline: 'none',
				},
			}),
			// Table with pagination
			PaginatedTable ? React.createElement(PaginatedTable, {
				key: 'variables-table',
				items: filteredVars,
				renderRow: renderRow,
				headers: tableHeaders,
				colgroup: colgroup,
				minItemsPerPage: 5,
				rowHeight: 70,
				maxHeight: '100%',
				className: 'variables-table',
				currentPage: this.getPage('variables'),
				onPageChange: (page: number) => this.setPage('variables', page),
			}) : React.createElement('div', null, 'Pagination not available'),
		);
	}

	openVariableEditDialog(variable: Variable): void {
		const dialogRoot = document.getElementById('variable-edit-dialog-root');
		if (!dialogRoot) return;

		const ReactDOM = (window as any).ReactDOM;
		const VariableEditDialog = (window as any).VariableEditDialog;
		if (!ReactDOM || !VariableEditDialog) return;

		const handleSave = (value: string, frozen: boolean) => {
			this.setVariableValue(variable, value);

			// Update freeze state
			if (frozen) {
				cheatFreezes.variables[variable.varname] = true;
			} else {
				delete cheatFreezes.variables[variable.varname];
			}

			// Close dialog
			if (ReactDOM.createRoot) {
				const root = (window as any).variableEditDialogRoot;
				if (root) root.render(null);
			} else {
				ReactDOM.render(null, dialogRoot);
			}

			// Force refresh of current view
			this.setState({ refreshKey: this.state.refreshKey + 1 });
		};

		const handleCancel = () => {
			if (ReactDOM.createRoot) {
				const root = (window as any).variableEditDialogRoot;
				if (root) root.render(null);
			} else {
				ReactDOM.render(null, dialogRoot);
			}
		};

		if (ReactDOM.createRoot) {
			let root = (window as any).variableEditDialogRoot;
			if (!root) {
				root = ReactDOM.createRoot(dialogRoot);
				(window as any).variableEditDialogRoot = root;
			}
			root.render(React.createElement(VariableEditDialog, {
				variable: variable,
				onSave: handleSave,
				onCancel: handleCancel,
			}));
		} else {
			ReactDOM.render(
				React.createElement(VariableEditDialog, {
					variable: variable,
					onSave: handleSave,
					onCancel: handleCancel,
				}),
				dialogRoot,
			);
		}
	}

	renderTimersTab(): ReactNode {
		if (!TheGame || !TheGame.Timers) return null;

		const PaginatedTable = (window as any).PaginatedTable;
		const filter = this.getFilter('timers');
		const sort = this.getSort('timers');
		const timerOrder = new Map<string, number>();
		TheGame.Timers.forEach((timer: Timer, index: number) => {
			timerOrder.set(timer.Name, index + 1);
		});

		// Filter and sort timers
		let filteredTimers = TheGame.Timers.filter((t: Timer) => {
			if (!filter) return true;
			const filterLower = filter.toLowerCase();
			return t.Name.toLowerCase().includes(filterLower);
		});

		const withIndex = filteredTimers.map((timer: Timer) => ({
			timer,
			order: timerOrder.get(timer.Name) || 0,
		}));
		withIndex.sort((a: { timer: Timer; order: number }, b: { timer: Timer; order: number }) => {
			const direction = sort.direction === 'asc' ? 1 : -1;
			const aTimer = a.timer;
			const bTimer = b.timer;
			let result = 0;
			switch (sort.key) {
				case 'order':
					result = a.order - b.order;
					break;
				case 'status':
					result = Number(aTimer.Active) - Number(bTimer.Active);
					break;
				case 'type':
					result = Number(aTimer.LiveTimer) - Number(bTimer.LiveTimer);
					break;
				case 'runs':
					result = aTimer.TurnNumber - bTimer.TurnNumber;
					break;
				case 'remaining': {
					const aRemaining = aTimer.LiveTimer && aTimer.Active
						? Math.ceil(((window as any).GameTimers?.getLiveTimerRemaining?.(aTimer) ?? 0) / 1000)
						: (aTimer.TType === 'TT_LENGTH' ? Math.max(0, aTimer.Length - aTimer.TurnNumber) : 0);
					const bRemaining = bTimer.LiveTimer && bTimer.Active
						? Math.ceil(((window as any).GameTimers?.getLiveTimerRemaining?.(bTimer) ?? 0) / 1000)
						: (bTimer.TType === 'TT_LENGTH' ? Math.max(0, bTimer.Length - bTimer.TurnNumber) : 0);
					result = aRemaining - bRemaining;
					break;
				}
				case 'name':
				default:
					result = aTimer.Name.toLowerCase().localeCompare(bTimer.Name.toLowerCase());
					break;
			}
			if (result === 0) {
				result = a.order - b.order;
			}
			return result * direction;
		});
		filteredTimers = withIndex.map((entry: { timer: Timer; order: number }) => entry.timer);

		const renderRow = (timer: Timer, index: number) => {
			let executedDisplay = `${timer.TurnNumber}`;
			let remainingDisplay = '-';
			if (timer.LiveTimer && timer.Active) {
				const remainingMs = (window as any).GameTimers?.getLiveTimerRemaining?.(timer) ?? 0;
				const secondsRemaining = Math.max(0, Math.ceil(remainingMs / 1000));
				remainingDisplay = `${secondsRemaining > 0 ? secondsRemaining : 0}s`;
			} else {
				if (timer.TType === 'TT_LENGTH') {
					const remainingTurns = Math.max(0, timer.Length - timer.TurnNumber);
					remainingDisplay = String(remainingTurns);
				} else if (timer.TType === 'TT_RUNALWAYS') {
					remainingDisplay = '0';
				}
			}

			const actionButtons: ReactNode[] = [];

			const startStopButton = timer.Active
				? React.createElement('button', {
					key: 'stop',
					className: 'btn timer-stop-btn',
					onClick: () => {
						timer.Active = false;
						if (timer.LiveTimer) {
							(window as any).GameTimers?.cancelLiveTimer?.(timer.Name);
							timer.curtickcount = 0;
						}
						(window as any).GameUI?.refreshPanelItems?.();
						(window as any).GameUI?.displayLiveTimers?.();
						this.forceUpdate();
					},
				}, 'Stop')
				: React.createElement('button', {
					key: 'start',
					className: 'btn timer-start-btn',
					onClick: () => {
						timer.Active = true;
						if (timer.LiveTimer) {
							(window as any).GameTimers?.scheduleLiveTimer?.(timer);
						}
						(window as any).GameUI?.refreshPanelItems?.();
						(window as any).GameUI?.displayLiveTimers?.();
						this.forceUpdate();
					},
				}, 'Start');

			if (timer.LiveTimer && timer.Active) {
				// Live timer: show Skip and Stop buttons
				actionButtons.push(
					React.createElement('button', {
						key: 'skip',
						className: 'btn timer-skip-btn',
						onClick: () => {
							(window as any).GameTimers?.skipLiveTimer?.(timer.Name);
							this.forceUpdate();
						},
					}, 'Skip'),
					startStopButton,
				);
			} else {
				// Static timer or inactive: show Start/Stop and Execute button
				actionButtons.push(
					startStopButton,
					React.createElement('button', {
						key: 'execute',
						className: 'btn',
						onClick: () => {
							this.executeTimer(timer);
						},
					}, 'Execute'),
				);
			}

			const orderValue = timerOrder.get(timer.Name) || 0;
			return React.createElement('tr', { key: timer.Name },
				React.createElement('td', null, orderValue ? String(orderValue) : '-'),
				React.createElement('td', null, timer.Name),
				React.createElement('td', null, timer.Active ? 'Active' : 'Inactive'),
				React.createElement('td', null, timer.LiveTimer ? 'Live' : 'Static'),
				React.createElement('td', null, executedDisplay),
				React.createElement('td', null, remainingDisplay),
				React.createElement('td', null,
					React.createElement('div', {
						style: {
							display: 'flex',
							gap: '6px',
							flexWrap: 'wrap',
						},
					}, actionButtons),
				),
			);
		};

		const colgroup = React.createElement('colgroup', null,
			React.createElement('col', { style: { width: '90px' } }), // Order
			React.createElement('col', { style: { width: '100%' } }), // Name
			React.createElement('col', { style: { width: '100px' } }), // Status
			React.createElement('col', { style: { width: '100px' } }), // Type
			React.createElement('col', { style: { width: '140px' } }), // Executed
			React.createElement('col', { style: { width: '160px' } }), // Remaining
			React.createElement('col', { style: { width: '190px' } }), // Actions
		);

		const tableHeaders = React.createElement('thead', null,
			React.createElement('tr', null,
				this.renderSortableHeader('timers', 'order', 'Order'),
				this.renderSortableHeader('timers', 'name', 'Name'),
				this.renderSortableHeader('timers', 'status', 'Status'),
				this.renderSortableHeader('timers', 'type', 'Type'),
				this.renderSortableHeader('timers', 'runs', 'Executed'),
				this.renderSortableHeader('timers', 'remaining', 'Remaining'),
				React.createElement('th', null, ''), // Empty header for actions column
			),
		);

		return React.createElement('div', {
				style: {
					display: 'flex',
					flexDirection: 'column',
					height: '100%',
					gap: '16px',
				},
			},
			React.createElement('input', {
				type: 'text',
				placeholder: 'Filter timers...',
				value: filter,
				onChange: this.handleFilterChange('timers'),
				style: {
					width: '100%',
					padding: '8px 12px',
					borderRadius: '4px',
					fontFamily: 'var(--font-game-text)',
					fontSize: '14px',
					background: 'var(--main-bg-color)',
					color: 'var(--main-text-color)',
					outline: 'none',
				},
			}),
			PaginatedTable ? React.createElement(PaginatedTable, {
				key: 'timers-table',
				items: filteredTimers,
				renderRow: renderRow,
				headers: tableHeaders,
				colgroup: colgroup,
				minItemsPerPage: 5,
				rowHeight: 70,
				maxHeight: '100%',
				className: 'timers-table',
				currentPage: this.getPage('timers'),
				onPageChange: (page: number) => this.setPage('timers', page),
			}) : React.createElement('div', null, 'Pagination not available'),
		);
	}

	executeTimer(timer: Timer): void {
		// Close cheat menu
		const ReactDOM = (window as any).ReactDOM;
		const dialogRoot = document.getElementById('cheat-dialog-root');
		if (dialogRoot && ReactDOM) {
			if (ReactDOM.createRoot) {
				const root = (window as any).cheatMenuRoot;
				if (root) root.render(null);
			} else {
				ReactDOM.render(null, dialogRoot);
			}
		}

		// Execute timer using GameTimers
		if ((window as any).GameTimers && typeof (window as any).GameTimers.runSingleTimer === 'function') {
			(window as any).GameTimers.runSingleTimer(timer, false); // false = don't check if active, just execute
		}
	}

	renderRoomsTab(): ReactNode {
		if (!TheGame || !TheGame.Rooms) return null;

		const PaginatedTable = (window as any).PaginatedTable;
		const filter = this.getFilter('rooms');
		const sort = this.getSort('rooms');

		// Get current room and surrounding rooms
		const currentRoom = Finder?.room?.(TheGame.Player.CurrentRoom);
		const surroundingRoomIds = new Set<string>();
		if (currentRoom && currentRoom.Exits) {
			currentRoom.Exits.forEach((exit: any) => {
				if (exit.DestinationRoom && exit.bActive) {
					surroundingRoomIds.add(exit.DestinationRoom);
				}
			});
		}

		// Filter rooms by location filter
		let filteredRooms = TheGame.Rooms.filter((r: Room) => {
			if (this.state.roomFilter === 'currentRoom') {
				if (!currentRoom || r.UniqueID !== currentRoom.UniqueID) return false;
			} else if (this.state.roomFilter === 'surroundingRooms') {
				if (!surroundingRoomIds.has(r.UniqueID)) return false;
			}
			// 'all' passes through

			// Apply text filter
			if (filter) {
				const filterLower = filter.toLowerCase();
				return r.Name.toLowerCase().includes(filterLower) ||
					(r.Description && r.Description.toLowerCase().includes(filterLower));
			}
			return true;
		});

		const withIndex = filteredRooms.map((room: Room, index: number) => ({ room, index }));
		withIndex.sort((a: { room: Room; index: number }, b: { room: Room; index: number }) => {
			const direction = sort.direction === 'asc' ? 1 : -1;
			const aRoom = a.room;
			const bRoom = b.room;
			let result = 0;
			switch (sort.key) {
				case 'group':
					result = (aRoom.Group || '').toLowerCase().localeCompare((bRoom.Group || '').toLowerCase());
					break;
				case 'name':
				default:
					result = aRoom.Name.toLowerCase().localeCompare(bRoom.Name.toLowerCase());
					break;
			}
			if (result === 0) {
				result = a.index - b.index;
			}
			return result * direction;
		});
		filteredRooms = withIndex.map((entry: { room: Room; index: number }) => entry.room);

		const renderRow = (room: Room, index: number) => {
			return React.createElement('tr', { key: room.UniqueID },
				React.createElement('td', null, room.Name),
				React.createElement('td', null, room.Group || 'None'),
				React.createElement('td', null,
					React.createElement('div', { style: { display: 'flex', gap: '4px' } },
						React.createElement('button', {
							className: 'btn',
							onClick: () => {
								this.openPropertiesDialog('room', room.UniqueID, room.Name, room.CustomProperties || []);
							},
						}, 'Properties'),
						React.createElement('button', {
							className: 'btn',
							onClick: () => {
								this.teleportToRoom(room);
							},
						}, 'Teleport'),
					),
				),
			);
		};

		const colgroup = React.createElement('colgroup', null,
			React.createElement('col', { style: { width: '50%' } }), // Name
			React.createElement('col', { style: { width: '50%' } }), // Group
			React.createElement('col', { style: { width: '210px' } }), // Actions
		);

		const tableHeaders = React.createElement('thead', null,
			React.createElement('tr', null,
				this.renderSortableHeader('rooms', 'name', 'Name'),
				this.renderSortableHeader('rooms', 'group', 'Group'),
				React.createElement('th', null, ''), // Empty header for actions column
			),
		);

		return React.createElement('div', {
				style: {
					display: 'flex',
					flexDirection: 'column',
					height: '100%',
					gap: '16px',
				},
			},
			React.createElement('input', {
				type: 'text',
				placeholder: 'Filter rooms...',
				value: filter,
				onChange: this.handleFilterChange('rooms'),
				style: {
					width: '100%',
					padding: '8px 12px',
					borderRadius: '4px',
					fontFamily: 'var(--font-game-text)',
					fontSize: '14px',
					background: 'var(--main-bg-color)',
					color: 'var(--main-text-color)',
					outline: 'none',
				},
			}),
			React.createElement('div', {
					style: {
						display: 'flex',
						gap: '8px',
						flexWrap: 'wrap',
						alignItems: 'center',
					},
				},
				React.createElement('button', {
					className: `btn ${this.state.roomFilter === 'all' ? 'active' : ''}`,
					onClick: () => this.setState({ roomFilter: 'all' }),
					style: {
						padding: '6px 12px',
						fontSize: '12px',
						backgroundColor: this.state.roomFilter === 'all' ? 'rgba(0, 0, 0, 0.15)' : 'transparent',
						border: this.state.roomFilter === 'all' ? '1px solid rgba(0, 0, 0, 0.3)' : '1px solid rgba(0, 0, 0, 0.2)',
						fontWeight: this.state.roomFilter === 'all' ? '600' : '400',
					},
				}, 'All Rooms'),
				React.createElement('button', {
					className: `btn ${this.state.roomFilter === 'currentRoom' ? 'active' : ''}`,
					onClick: () => this.setState({ roomFilter: 'currentRoom' }),
					disabled: !currentRoom,
					style: {
						padding: '6px 12px',
						fontSize: '12px',
						backgroundColor: this.state.roomFilter === 'currentRoom' ? 'rgba(0, 0, 0, 0.15)' : 'transparent',
						border: this.state.roomFilter === 'currentRoom' ? '1px solid rgba(0, 0, 0, 0.3)' : '1px solid rgba(0, 0, 0, 0.2)',
						fontWeight: this.state.roomFilter === 'currentRoom' ? '600' : '400',
						opacity: currentRoom ? 1 : 0.5,
					},
				}, 'Current Room'),
				React.createElement('button', {
					className: `btn ${this.state.roomFilter === 'surroundingRooms' ? 'active' : ''}`,
					onClick: () => this.setState({ roomFilter: 'surroundingRooms' }),
					disabled: surroundingRoomIds.size === 0,
					style: {
						padding: '6px 12px',
						fontSize: '12px',
						backgroundColor: this.state.roomFilter === 'surroundingRooms' ? 'rgba(0, 0, 0, 0.15)' : 'transparent',
						border: this.state.roomFilter === 'surroundingRooms' ? '1px solid rgba(0, 0, 0, 0.3)' : '1px solid rgba(0, 0, 0, 0.2)',
						fontWeight: this.state.roomFilter === 'surroundingRooms' ? '600' : '400',
						opacity: surroundingRoomIds.size > 0 ? 1 : 0.5,
					},
				}, `Surrounding Rooms (${surroundingRoomIds.size})`),
			),
			PaginatedTable ? React.createElement(PaginatedTable, {
				key: 'rooms-table',
				items: filteredRooms,
				renderRow: renderRow,
				headers: tableHeaders,
				colgroup: colgroup,
				minItemsPerPage: 5,
				rowHeight: 70,
				maxHeight: '100%',
				className: 'rooms-table',
				currentPage: this.getPage('rooms'),
				onPageChange: (page: number) => this.setPage('rooms', page),
			}) : React.createElement('div', null, 'Pagination not available'),
		);
	}

	teleportToRoom(room: Room): void {
		// Clear moving direction to prevent showing a fake direction
		if ((window as any).Globals) {
			(window as any).Globals.movingDirection = '';
		}

		// Close cheat menu
		const ReactDOM = (window as any).ReactDOM;
		const dialogRoot = document.getElementById('cheat-dialog-root');
		if (dialogRoot && ReactDOM) {
			if (ReactDOM.createRoot) {
				const root = (window as any).cheatMenuRoot;
				if (root) root.render(null);
			} else {
				ReactDOM.render(null, dialogRoot);
			}
		}

		// Teleport using ChangeRoom with bQuiet=true to prevent showing direction
		if ((window as any).ChangeRoom && typeof (window as any).ChangeRoom === 'function') {
			(window as any).ChangeRoom(room, true, true, true);
		}
	}

	renderObjectsTab(): ReactNode {
		if (!TheGame || !TheGame.Objects) return null;

		const PaginatedTable = (window as any).PaginatedTable;
		const filter = this.getFilter('objects');
		const sort = this.getSort('objects');

		// Get current room for filtering
		const currentRoom = Finder?.room?.(TheGame.Player.CurrentRoom);
		const currentRoomId = currentRoom?.UniqueID || '';

		// Get unique rooms for dropdown
		const roomsWithObjects = new Map<string, Room>();
		TheGame.Objects.forEach((obj: RagsObject) => {
			if (obj.locationtype === 'LT_ROOM' && obj.locationname) {
				const room = Finder?.room?.(obj.locationname);
				if (room && !roomsWithObjects.has(room.UniqueID)) {
					roomsWithObjects.set(room.UniqueID, room);
				}
			}
		});
		const sortedRooms = Array.from(roomsWithObjects.values()).sort((a, b) =>
			a.Name.toLowerCase().localeCompare(b.Name.toLowerCase()),
		);

		// Get unique characters for dropdown
		const charactersWithObjects = new Map<string, Character>();
		TheGame.Objects.forEach((obj: RagsObject) => {
			if (obj.locationtype === 'LT_CHARACTER' && obj.locationname) {
				const char = Finder?.character?.(obj.locationname);
				if (char && !charactersWithObjects.has(char.Charname)) {
					charactersWithObjects.set(char.Charname, char);
				}
			}
		});
		const sortedCharacters = Array.from(charactersWithObjects.values()).sort((a, b) =>
			a.Charname.toLowerCase().localeCompare(b.Charname.toLowerCase()),
		);

		// Filter objects by location
		let filteredObjects = TheGame.Objects.filter((obj: RagsObject) => {
			// Apply location filter
			if (this.state.objectLocationFilter === 'playerInventory') {
				if (obj.locationtype !== 'LT_PLAYER') return false;
			} else if (this.state.objectLocationFilter === 'currentRoom') {
				if (obj.locationtype !== 'LT_ROOM' || obj.locationname !== currentRoomId) return false;
			} else if (this.state.objectLocationFilter !== 'all') {
				// Check if it's a room ID
				const room = Finder?.room?.(this.state.objectLocationFilter);
				if (room) {
					// It's a room ID
					if (obj.locationtype !== 'LT_ROOM' || obj.locationname !== this.state.objectLocationFilter) return false;
				} else {
					// Check if it's a character name
					const char = sortedCharacters.find((c: Character) => c.Charname === this.state.objectLocationFilter);
					if (char) {
						// It's a character name - need to check if object is in this character's inventory
						if (obj.locationtype !== 'LT_CHARACTER') return false;
						const objChar = Finder?.character?.(obj.locationname);
						if (!objChar || objChar.Charname !== this.state.objectLocationFilter) return false;
					} else {
						// Unknown filter value, skip
						return false;
					}
				}
			}
			// 'all' passes through

			// Apply text filter
			if (filter) {
				const filterLower = filter.toLowerCase();
				return obj.name.toLowerCase().includes(filterLower);
			}
			return true;
		});

		const withIndex = filteredObjects.map((obj: RagsObject, index: number) => ({ obj, index }));
		withIndex.sort((a: { obj: RagsObject; index: number }, b: { obj: RagsObject; index: number }) => {
			const direction = sort.direction === 'asc' ? 1 : -1;
			const aObj = a.obj;
			const bObj = b.obj;
			let result = 0;
			switch (sort.key) {
				case 'location':
					result = this.getObjectLocationText(aObj).toLowerCase().localeCompare(this.getObjectLocationText(bObj).toLowerCase());
					break;
				case 'name':
				default:
					result = aObj.name.toLowerCase().localeCompare(bObj.name.toLowerCase());
					break;
			}
			if (result === 0) {
				result = a.index - b.index;
			}
			return result * direction;
		});
		filteredObjects = withIndex.map((entry: { obj: RagsObject; index: number }) => entry.obj);

		const renderRow = (obj: RagsObject, index: number) => {
			const locationText = this.getObjectLocationText(obj);
			return React.createElement('tr', { key: obj.UniqueIdentifier },
				React.createElement('td', null, obj.name),
				React.createElement('td', null, locationText),
				React.createElement('td', null,
					React.createElement('div', { style: { display: 'flex', gap: '4px' } },
						React.createElement('button', {
							className: 'btn',
							onClick: () => {
								this.openPropertiesDialog('object', obj.UniqueIdentifier, obj.name, obj.CustomProperties || []);
							},
						}, 'Properties'),
						React.createElement('button', {
							className: 'btn',
							onClick: () => {
								this.openMoveObjectDialog(obj);
							},
						}, 'Move'),
					),
				),
			);
		};

		const colgroup = React.createElement('colgroup', null,
			React.createElement('col', { style: { width: '50%' } }), // Name
			React.createElement('col', { style: { width: '50%' } }), // Location
			React.createElement('col', { style: { width: '210px' } }), // Actions
		);

		const tableHeaders = React.createElement('thead', null,
			React.createElement('tr', null,
				this.renderSortableHeader('objects', 'name', 'Name'),
				this.renderSortableHeader('objects', 'location', 'Location'),
				React.createElement('th', null, ''), // Empty header for actions column
			),
		);

		return React.createElement('div', {
				style: {
					display: 'flex',
					flexDirection: 'column',
					height: '100%',
					gap: '16px',
				},
			},
			React.createElement('input', {
				type: 'text',
				placeholder: 'Filter objects...',
				value: filter,
				onChange: this.handleFilterChange('objects'),
				style: {
					width: '100%',
					padding: '8px 12px',
					borderRadius: '4px',
					fontFamily: 'var(--font-game-text)',
					fontSize: '14px',
					background: 'var(--main-bg-color)',
					color: 'var(--main-text-color)',
					outline: 'none',
				},
			}),
			React.createElement('div', {
					style: {
						display: 'flex',
						gap: '8px',
						flexWrap: 'wrap',
						alignItems: 'center',
					},
				},
				React.createElement('button', {
					className: `btn ${this.state.objectLocationFilter === 'all' ? 'active' : ''}`,
					onClick: () => this.setState({ objectLocationFilter: 'all' }),
					style: {
						padding: '6px 12px',
						fontSize: '12px',
						backgroundColor: this.state.objectLocationFilter === 'all' ? 'rgba(0, 0, 0, 0.15)' : 'transparent',
						border: this.state.objectLocationFilter === 'all' ? '1px solid rgba(0, 0, 0, 0.3)' : '1px solid rgba(0, 0, 0, 0.2)',
						fontWeight: this.state.objectLocationFilter === 'all' ? '600' : '400',
					},
				}, 'All Objects'),
				React.createElement('button', {
					className: `btn ${this.state.objectLocationFilter === 'playerInventory' ? 'active' : ''}`,
					onClick: () => this.setState({ objectLocationFilter: 'playerInventory' }),
					style: {
						padding: '6px 12px',
						fontSize: '12px',
						backgroundColor: this.state.objectLocationFilter === 'playerInventory' ? 'rgba(0, 0, 0, 0.15)' : 'transparent',
						border: this.state.objectLocationFilter === 'playerInventory' ? '1px solid rgba(0, 0, 0, 0.3)' : '1px solid rgba(0, 0, 0, 0.2)',
						fontWeight: this.state.objectLocationFilter === 'playerInventory' ? '600' : '400',
					},
				}, 'In Player Inventory'),
				React.createElement('button', {
					className: `btn ${this.state.objectLocationFilter === 'currentRoom' ? 'active' : ''}`,
					onClick: () => this.setState({ objectLocationFilter: 'currentRoom' }),
					disabled: !currentRoom,
					style: {
						padding: '6px 12px',
						fontSize: '12px',
						backgroundColor: this.state.objectLocationFilter === 'currentRoom' ? 'rgba(0, 0, 0, 0.15)' : 'transparent',
						border: this.state.objectLocationFilter === 'currentRoom' ? '1px solid rgba(0, 0, 0, 0.3)' : '1px solid rgba(0, 0, 0, 0.2)',
						fontWeight: this.state.objectLocationFilter === 'currentRoom' ? '600' : '400',
						opacity: currentRoom ? 1 : 0.5,
					},
				}, 'In Current Room'),
				React.createElement('select', {
						value: this.state.objectLocationFilter !== 'all' &&
						this.state.objectLocationFilter !== 'playerInventory' &&
						this.state.objectLocationFilter !== 'currentRoom' &&
						!sortedCharacters.find((c: Character) => c.Charname === this.state.objectLocationFilter)
							? this.state.objectLocationFilter : '',
						onChange: (e: any) => {
							const value = e.target.value;
							this.setState({ objectLocationFilter: value || 'all' });
						},
						style: {
							padding: '6px 12px',
							fontSize: '12px',
							borderRadius: '4px',
							fontFamily: 'var(--font-game-text)',
							background: 'var(--main-bg-color)',
							color: 'var(--main-text-color)',
							border: this.state.objectLocationFilter !== 'all' &&
							this.state.objectLocationFilter !== 'playerInventory' &&
							this.state.objectLocationFilter !== 'currentRoom' &&
							!sortedCharacters.find((c: Character) => c.Charname === this.state.objectLocationFilter)
								? '1px solid rgba(0, 0, 0, 0.3)' : '1px solid rgba(0, 0, 0, 0.2)',
							outline: 'none',
							flexShrink: 0,
							width: '200px',
							maxWidth: '200px',
							minWidth: '200px',
						},
					},
					React.createElement('option', { value: '' }, 'Select Room...'),
					sortedRooms.map((room: Room) =>
						React.createElement('option', { key: room.UniqueID, value: room.UniqueID }, room.Name),
					),
				),
				React.createElement('select', {
						value: sortedCharacters.find((c: Character) => c.Charname === this.state.objectLocationFilter)
							? this.state.objectLocationFilter : '',
						onChange: (e: any) => {
							const value = e.target.value;
							this.setState({ objectLocationFilter: value || 'all' });
						},
						style: {
							padding: '6px 12px',
							fontSize: '12px',
							borderRadius: '4px',
							fontFamily: 'var(--font-game-text)',
							background: 'var(--main-bg-color)',
							color: 'var(--main-text-color)',
							border: sortedCharacters.find((c: Character) => c.Charname === this.state.objectLocationFilter)
								? '1px solid rgba(0, 0, 0, 0.3)' : '1px solid rgba(0, 0, 0, 0.2)',
							outline: 'none',
							flexShrink: 0,
							width: '200px',
							maxWidth: '200px',
							minWidth: '200px',
						},
					},
					React.createElement('option', { value: '' }, 'Select Character...'),
					sortedCharacters.map((char: Character) =>
						React.createElement('option', { key: char.Charname, value: char.Charname }, char.Charname),
					),
				),
			),
			PaginatedTable ? React.createElement(PaginatedTable, {
				key: 'objects-table',
				items: filteredObjects,
				renderRow: renderRow,
				headers: tableHeaders,
				colgroup: colgroup,
				minItemsPerPage: 5,
				rowHeight: 70,
				maxHeight: '100%',
				className: 'objects-table',
				currentPage: this.getPage('objects'),
				onPageChange: (page: number) => this.setPage('objects', page),
			}) : React.createElement('div', null, 'Pagination not available'),
		);
	}

	getObjectLocationText(obj: RagsObject): string {
		if (obj.locationtype === 'LT_NULL') return 'None';
		if (obj.locationtype === 'LT_PLAYER') return 'Player Inventory';
		if (obj.locationtype === 'LT_ROOM') {
			const room = Finder?.room?.(obj.locationname);
			return room ? room.Name : obj.locationname;
		}
		if (obj.locationtype === 'LT_CHARACTER') {
			const char = Finder?.character?.(obj.locationname);
			return char ? char.Charname : obj.locationname;
		}
		if (obj.locationtype === 'LT_IN_OBJECT') {
			const container = Finder?.object?.(obj.locationname);
			return container ? `In: ${container.name}` : obj.locationname;
		}
		return obj.locationname || 'Unknown';
	}

	openMoveObjectDialog(obj: RagsObject): void {
		const dialogRoot = document.getElementById('move-object-dialog-root');
		if (!dialogRoot) return;

		const ReactDOM = (window as any).ReactDOM;
		const MoveObjectDialog = (window as any).MoveObjectDialog;
		if (!ReactDOM || !MoveObjectDialog) return;

		const handleSave = (locationType: string, locationName: string) => {
			// Use GameCommands to move the object properly
			const Command = (window as any).Command;
			const GameCommands = (window as any).GameCommands;

			if (GameCommands && typeof GameCommands.runSingleCommand === 'function' && Command) {
				let command: any;
				let part2 = '';
				let part3 = '';
				let part4 = '';

				if (locationType === 'LT_PLAYER') {
					command = new Command();
					command.cmdtype = 'CT_MOVEITEMTOINV';
					part2 = obj.UniqueIdentifier;
					part3 = '';
					part4 = '';
				} else if (locationType === 'LT_CHARACTER') {
					command = new Command();
					command.cmdtype = 'CT_MOVEITEMTOCHAR';
					part2 = obj.UniqueIdentifier;
					part3 = locationName;
					part4 = '';
				} else if (locationType === 'LT_IN_OBJECT') {
					command = new Command();
					command.cmdtype = 'CT_MOVEITEMTOOBJ';
					part2 = obj.UniqueIdentifier;
					part3 = locationName;
					part4 = '';
				} else if (locationType === 'LT_ROOM') {
					command = new Command();
					command.cmdtype = 'CT_MOVEITEMTOROOM';
					part2 = obj.UniqueIdentifier;
					const CurrentRoomGuid = (window as any).CurrentRoomGuid || '00000000-0000-0000-0000-000000000001';
					part3 = locationName || CurrentRoomGuid;
					part4 = '';
				} else if (locationType === 'LT_NULL') {
					obj.locationtype = 'LT_NULL';
					obj.locationname = '';
				}

				if (command) {
					GameCommands.runSingleCommand(command, part2, part3, part4, '');
				}
			} else {
				// Fallback: directly set location
				obj.locationtype = locationType;
				obj.locationname = locationName;
			}

			// Close dialog
			if (ReactDOM.createRoot) {
				const root = (window as any).moveObjectDialogRoot;
				if (root) root.render(null);
			} else {
				ReactDOM.render(null, dialogRoot);
			}

			// Force refresh of current view
			this.setState({ refreshKey: this.state.refreshKey + 1 });

			// Refresh UI
			if ((window as any).GameUI) {
				(window as any).GameUI.refreshInventory?.();
				(window as any).GameUI.refreshPanelItems?.();
			}
		};

		const handleCancel = () => {
			if (ReactDOM.createRoot) {
				const root = (window as any).moveObjectDialogRoot;
				if (root) root.render(null);
			} else {
				ReactDOM.render(null, dialogRoot);
			}
		};

		if (ReactDOM.createRoot) {
			let root = (window as any).moveObjectDialogRoot;
			if (!root) {
				root = ReactDOM.createRoot(dialogRoot);
				(window as any).moveObjectDialogRoot = root;
			}
			root.render(React.createElement(MoveObjectDialog, {
				object: obj,
				onSave: handleSave,
				onCancel: handleCancel,
			}));
		} else {
			ReactDOM.render(
				React.createElement(MoveObjectDialog, {
					object: obj,
					onSave: handleSave,
					onCancel: handleCancel,
				}),
				dialogRoot,
			);
		}
	}

	renderCharactersTab(): ReactNode {
		if (!TheGame || !TheGame.Characters) return null;

		const PaginatedTable = (window as any).PaginatedTable;
		const filter = this.getFilter('characters');
		const sort = this.getSort('characters');

		// Get current room for filtering
		const currentRoom = Finder?.room?.(TheGame.Player.CurrentRoom);
		const currentRoomId = currentRoom?.UniqueID || '';

		// Filter characters by location
		let filteredChars = TheGame.Characters.filter((c: Character) => {
			// Apply location filter
			if (this.state.characterLocationFilter === 'currentRoom') {
				if (c.CurrentRoom !== currentRoomId) return false;
			} else if (this.state.characterLocationFilter !== 'all') {
				// Specific room ID
				if (c.CurrentRoom !== this.state.characterLocationFilter) return false;
			}
			// 'all' passes through

			// Apply text filter
			if (filter) {
				const filterLower = filter.toLowerCase();
				return c.Charname.toLowerCase().includes(filterLower);
			}
			return true;
		});

		const withIndex = filteredChars.map((char: Character, index: number) => ({ char, index }));
		withIndex.sort((a: { char: Character; index: number }, b: { char: Character; index: number }) => {
			const direction = sort.direction === 'asc' ? 1 : -1;
			const aChar = a.char;
			const bChar = b.char;
			let result = 0;
			switch (sort.key) {
				case 'location': {
					const aRoom = Finder?.room?.(aChar.CurrentRoom);
					const bRoom = Finder?.room?.(bChar.CurrentRoom);
					const aName = (aRoom ? aRoom.Name : aChar.CurrentRoom) || '';
					const bName = (bRoom ? bRoom.Name : bChar.CurrentRoom) || '';
					result = aName.toLowerCase().localeCompare(bName.toLowerCase());
					break;
				}
				case 'name':
				default:
					result = aChar.Charname.toLowerCase().localeCompare(bChar.Charname.toLowerCase());
					break;
			}
			if (result === 0) {
				result = a.index - b.index;
			}
			return result * direction;
		});
		filteredChars = withIndex.map((entry: { char: Character; index: number }) => entry.char);

		// Get unique rooms for dropdown
		const roomsWithCharacters = new Map<string, Room>();
		TheGame.Characters.forEach((char: Character) => {
			if (char.CurrentRoom) {
				const room = Finder?.room?.(char.CurrentRoom);
				if (room && !roomsWithCharacters.has(room.UniqueID)) {
					roomsWithCharacters.set(room.UniqueID, room);
				}
			}
		});
		const sortedRooms = Array.from(roomsWithCharacters.values()).sort((a, b) =>
			a.Name.toLowerCase().localeCompare(b.Name.toLowerCase()),
		);

		const renderRow = (char: Character, index: number) => {
			const room = Finder?.room?.(char.CurrentRoom);
			const roomName = room ? room.Name : char.CurrentRoom;

			return React.createElement('tr', { key: char.Charname },
				React.createElement('td', null, char.Charname),
				React.createElement('td', null, roomName || 'None'),
				React.createElement('td', null,
					React.createElement('div', { style: { display: 'flex', gap: '4px' } },
						React.createElement('button', {
							className: 'btn',
							onClick: () => {
								this.openPropertiesDialog('character', char.Charname, char.Charname, char.CustomProperties || []);
							},
						}, 'Properties'),
						React.createElement('button', {
							className: 'btn',
							onClick: () => {
								this.openMoveCharacterDialog(char);
							},
						}, 'Move'),
					),
				),
			);
		};

		const colgroup = React.createElement('colgroup', null,
			React.createElement('col', { style: { width: '50%' } }), // Name
			React.createElement('col', { style: { width: '50%' } }), // Location
			React.createElement('col', { style: { width: '210px' } }), // Actions
		);

		const tableHeaders = React.createElement('thead', null,
			React.createElement('tr', null,
				this.renderSortableHeader('characters', 'name', 'Name'),
				this.renderSortableHeader('characters', 'location', 'Location'),
				React.createElement('th', null, ''), // Empty header for actions column
			),
		);

		return React.createElement('div', {
				style: {
					display: 'flex',
					flexDirection: 'column',
					height: '100%',
					gap: '16px',
				},
			},
			React.createElement('input', {
				type: 'text',
				placeholder: 'Filter characters...',
				value: filter,
				onChange: this.handleFilterChange('characters'),
				style: {
					width: '100%',
					padding: '8px 12px',
					borderRadius: '4px',
					fontFamily: 'var(--font-game-text)',
					fontSize: '14px',
					background: 'var(--main-bg-color)',
					color: 'var(--main-text-color)',
					outline: 'none',
				},
			}),
			React.createElement('div', {
					style: {
						display: 'flex',
						gap: '8px',
						flexWrap: 'wrap',
						alignItems: 'center',
					},
				},
				React.createElement('button', {
					className: `btn ${this.state.characterLocationFilter === 'all' ? 'active' : ''}`,
					onClick: () => this.setState({ characterLocationFilter: 'all' }),
					style: {
						padding: '6px 12px',
						fontSize: '12px',
						backgroundColor: this.state.characterLocationFilter === 'all' ? 'rgba(0, 0, 0, 0.15)' : 'transparent',
						border: this.state.characterLocationFilter === 'all' ? '1px solid rgba(0, 0, 0, 0.3)' : '1px solid rgba(0, 0, 0, 0.2)',
						fontWeight: this.state.characterLocationFilter === 'all' ? '600' : '400',
					},
				}, 'All Characters'),
				React.createElement('button', {
					className: `btn ${this.state.characterLocationFilter === 'currentRoom' ? 'active' : ''}`,
					onClick: () => this.setState({ characterLocationFilter: 'currentRoom' }),
					disabled: !currentRoom,
					style: {
						padding: '6px 12px',
						fontSize: '12px',
						backgroundColor: this.state.characterLocationFilter === 'currentRoom' ? 'rgba(0, 0, 0, 0.15)' : 'transparent',
						border: this.state.characterLocationFilter === 'currentRoom' ? '1px solid rgba(0, 0, 0, 0.3)' : '1px solid rgba(0, 0, 0, 0.2)',
						fontWeight: this.state.characterLocationFilter === 'currentRoom' ? '600' : '400',
						opacity: currentRoom ? 1 : 0.5,
					},
				}, 'In Current Room'),
				React.createElement('select', {
						value: this.state.characterLocationFilter !== 'all' &&
						this.state.characterLocationFilter !== 'currentRoom'
							? this.state.characterLocationFilter : '',
						onChange: (e: any) => {
							const value = e.target.value;
							this.setState({ characterLocationFilter: value || 'all' });
						},
						style: {
							padding: '6px 12px',
							fontSize: '12px',
							borderRadius: '4px',
							fontFamily: 'var(--font-game-text)',
							background: 'var(--main-bg-color)',
							color: 'var(--main-text-color)',
							border: this.state.characterLocationFilter !== 'all' &&
							this.state.characterLocationFilter !== 'currentRoom'
								? '1px solid rgba(0, 0, 0, 0.3)' : '1px solid rgba(0, 0, 0, 0.2)',
							outline: 'none',
							flexShrink: 0,
							width: '200px',
							maxWidth: '200px',
							minWidth: '200px',
						},
					},
					React.createElement('option', { value: '' }, 'Select Room...'),
					sortedRooms.map((room: Room) =>
						React.createElement('option', { key: room.UniqueID, value: room.UniqueID }, room.Name),
					),
				),
			),
			PaginatedTable ? React.createElement(PaginatedTable, {
				key: 'characters-table',
				items: filteredChars,
				renderRow: renderRow,
				headers: tableHeaders,
				colgroup: colgroup,
				minItemsPerPage: 5,
				rowHeight: 70,
				maxHeight: '100%',
				className: 'characters-table',
				currentPage: this.getPage('characters'),
				onPageChange: (page: number) => this.setPage('characters', page),
			}) : React.createElement('div', null, 'Pagination not available'),
		);
	}

	openMoveCharacterDialog(char: Character): void {
		const dialogRoot = document.getElementById('move-character-dialog-root');
		if (!dialogRoot) return;

		const ReactDOM = (window as any).ReactDOM;
		const MoveCharacterDialog = (window as any).MoveCharacterDialog;
		if (!ReactDOM || !MoveCharacterDialog) return;

		const handleSave = (roomId: string) => {
			// Use GameCommands to move the character properly
			const Command = (window as any).Command;
			const GameCommands = (window as any).GameCommands;

			if (GameCommands && typeof GameCommands.runSingleCommand === 'function' && Command) {
				const CurrentRoomGuid = (window as any).CurrentRoomGuid || '00000000-0000-0000-0000-000000000001';
				const targetRoomId = roomId || CurrentRoomGuid;

				const command = new Command();
				command.cmdtype = 'CT_MOVECHAR';
				GameCommands.runSingleCommand(command, char.Charname, targetRoomId, '', '');
			} else {
				// Fallback: directly set room
				char.CurrentRoom = roomId;
			}

			// Close dialog
			if (ReactDOM.createRoot) {
				const root = (window as any).moveCharacterDialogRoot;
				if (root) root.render(null);
			} else {
				ReactDOM.render(null, dialogRoot);
			}

			// Force refresh of current view
			this.setState({ refreshKey: this.state.refreshKey + 1 });

			// Refresh UI
			if ((window as any).GameUI) {
				(window as any).GameUI.refreshCharacters?.();
			}
		};

		const handleCancel = () => {
			if (ReactDOM.createRoot) {
				const root = (window as any).moveCharacterDialogRoot;
				if (root) root.render(null);
			} else {
				ReactDOM.render(null, dialogRoot);
			}
		};

		if (ReactDOM.createRoot) {
			let root = (window as any).moveCharacterDialogRoot;
			if (!root) {
				root = ReactDOM.createRoot(dialogRoot);
				(window as any).moveCharacterDialogRoot = root;
			}
			root.render(React.createElement(MoveCharacterDialog, {
				character: char,
				onSave: handleSave,
				onCancel: handleCancel,
			}));
		} else {
			ReactDOM.render(
				React.createElement(MoveCharacterDialog, {
					character: char,
					onSave: handleSave,
					onCancel: handleCancel,
				}),
				dialogRoot,
			);
		}
	}

	openPropertiesDialog(entityType: 'room' | 'object' | 'character' | 'variable' | 'player', entityId: string, entityName: string, properties: CustomProperty[]): void {
		const dialogRoot = document.getElementById('properties-dialog-root');
		if (!dialogRoot) return;

		const ReactDOM = (window as any).ReactDOM;
		const PropertiesDialog = (window as any).PropertiesDialog;
		if (!ReactDOM || !PropertiesDialog) return;

		const handleSave = (updatedProperties: CustomProperty[]) => {
			// Properties are already updated in the dialog
			// Close dialog
			if (ReactDOM.createRoot) {
				const root = (window as any).propertiesDialogRoot;
				if (root) root.render(null);
			} else {
				ReactDOM.render(null, dialogRoot);
			}

			// Force refresh of current view
			this.setState({ refreshKey: this.state.refreshKey + 1 });
		};

		const handleCancel = () => {
			if (ReactDOM.createRoot) {
				const root = (window as any).propertiesDialogRoot;
				if (root) root.render(null);
			} else {
				ReactDOM.render(null, dialogRoot);
			}
		};

		if (ReactDOM.createRoot) {
			let root = (window as any).propertiesDialogRoot;
			if (!root) {
				root = ReactDOM.createRoot(dialogRoot);
				(window as any).propertiesDialogRoot = root;
			}
			root.render(React.createElement(PropertiesDialog, {
				entityType: entityType,
				entityId: entityId,
				entityName: entityName,
				properties: properties,
				onSave: handleSave,
				onCancel: handleCancel,
			}));
		} else {
			ReactDOM.render(
				React.createElement(PropertiesDialog, {
					entityType: entityType,
					entityId: entityId,
					entityName: entityName,
					properties: properties,
					onSave: handleSave,
					onCancel: handleCancel,
				}),
				dialogRoot,
			);
		}
	}

	renderPlayerTab(): ReactNode {
		if (!TheGame || !TheGame.Player) return null;

		const PaginatedTable = (window as any).PaginatedTable;
		const filter = this.getFilter('player');
		const sort = this.getSort('player');

		const allProperties = TheGame.Player.CustomProperties || [];
		const hasProperties = allProperties.length > 0;

		// Filter and sort custom properties
		let filteredProperties = allProperties.filter((prop: CustomProperty) => {
			if (!filter) return true;
			const filterLower = filter.toLowerCase();
			return prop.Name.toLowerCase().includes(filterLower);
		});

		const withIndex = filteredProperties.map((property: CustomProperty, index: number) => ({ property, index }));
		withIndex.sort((a: { property: CustomProperty; index: number }, b: { property: CustomProperty; index: number }) => {
			const direction = sort.direction === 'asc' ? 1 : -1;
			const aProp = a.property;
			const bProp = b.property;
			let result = 0;
			switch (sort.key) {
				case 'value':
					result = (aProp.Value || '').toLowerCase().localeCompare((bProp.Value || '').toLowerCase());
					break;
				case 'name':
				default:
					result = aProp.Name.toLowerCase().localeCompare(bProp.Name.toLowerCase());
					break;
			}
			if (result === 0) {
				result = a.index - b.index;
			}
			return result * direction;
		});
		filteredProperties = withIndex.map((entry: { property: CustomProperty; index: number }) => entry.property);

		const renderRow = (property: CustomProperty, index: number) => {
			// Initialize cheatFreezes.customProperties if needed
			if (!cheatFreezes.customProperties) {
				(cheatFreezes as any).customProperties = {};
			}
			if (!cheatFreezes.customProperties!['player']) {
				cheatFreezes.customProperties!['player'] = {};
			}

			const isFrozen = !!(cheatFreezes.customProperties!['player']?.[property.Name]);
			const value = property.Value || '';

			return React.createElement('tr', {
					key: property.Name,
					style: { height: '70px' },
				},
				React.createElement('td', {
						style: {
							height: '70px',
							lineHeight: '1.33rem',
							overflow: 'hidden',
							textOverflow: 'ellipsis',
							whiteSpace: 'nowrap',
						},
					},
					React.createElement('span', null, property.Name),
					isFrozen && React.createElement('span', {
						style: {
							marginLeft: '8px',
							color: '#ff6b6b',
							fontSize: '12px',
						},
						title: 'Frozen',
					}, '🔒'),
				),
				React.createElement('td', {
						style: {
							fontFamily: 'monospace',
							fontSize: '12px',
							height: '70px',
							lineHeight: '1.33rem',
						},
					},
					React.createElement('span', {
						style: {
							display: '-webkit-box',
							WebkitLineClamp: 2,
							WebkitBoxOrient: 'vertical',
							overflow: 'hidden',
							height: '2.8rem',
							lineHeight: '1.33rem',
						},
					}, value),
				),
				React.createElement('td', {
						style: {
							height: '70px',
							lineHeight: '1.33rem',
						},
					},
					React.createElement('button', {
						className: 'btn',
						onClick: () => {
							this.openPlayerPropertyEditDialog(property);
						},
					}, 'Edit'),
				),
			);
		};

		const colgroup = React.createElement('colgroup', null,
			React.createElement('col', { style: { width: '100%' } }), // Name - takes remaining space
			React.createElement('col', { style: { width: '8rem' } }), // Value - same width as variables tab
			React.createElement('col', { style: { width: '80px' } }), // Actions
		);

		const tableHeaders = React.createElement('thead', null,
			React.createElement('tr', null,
				this.renderSortableHeader('player', 'name', 'Name'),
				this.renderSortableHeader('player', 'value', 'Value'),
				React.createElement('th', null, ''), // Empty header for actions column
			),
		);

		return React.createElement('div', {
				style: {
					display: 'flex',
					flexDirection: 'column',
					height: '100%',
					gap: '20px',
				},
			},
			React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: '8px' } },
				React.createElement('div', {
						style: {
							display: 'flex',
							justifyContent: 'space-between',
							alignItems: 'center',
						},
					},
					React.createElement('div', {
							style: {
								display: 'flex',
								flexDirection: 'column',
								gap: '4px',
								flex: 1,
							},
						},
						React.createElement('label', { style: { fontWeight: 'bold' } }, 'Name:'),
						React.createElement('span', null, TheGame.Player.Name || TheGame.Player.PlayerName || 'Unnamed'),
					),
					React.createElement('button', {
						className: 'btn',
						onClick: () => {
							this.openPlayerEditDialog();
						},
						style: { marginLeft: '16px' },
					}, 'Edit'),
				),
			),
			React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: '8px' } },
				React.createElement('div', {
						style: {
							display: 'flex',
							justifyContent: 'space-between',
							alignItems: 'center',
						},
					},
					React.createElement('div', {
							style: {
								display: 'flex',
								flexDirection: 'column',
								gap: '4px',
								flex: 1,
							},
						},
						React.createElement('label', { style: { fontWeight: 'bold' } }, 'Gender:'),
						React.createElement('span', null, TheGame.Player.Gender || TheGame.Player.PlayerGender || 'Unknown'),
					),
					React.createElement('button', {
						className: 'btn',
						onClick: () => {
							this.openPlayerEditDialog();
						},
						style: { marginLeft: '16px' },
					}, 'Edit'),
				),
			),
			React.createElement('div', { style: { display: 'flex', justifyContent: 'flex-end' } },
				React.createElement('button', {
					className: 'btn',
					onClick: () => {
						this.openPropertiesDialog('player', 'player', TheGame.Player.Name || TheGame.Player.PlayerName || 'Player', TheGame.Player.CustomProperties || []);
					},
				}, 'Properties'),
			),
			hasProperties && React.createElement('div', {
					style: {
						display: 'flex',
						flexDirection: 'column',
						gap: '16px',
						flex: 1,
						minHeight: 0,
					},
				},
				React.createElement('h3', {
					style: {
						margin: 0,
						fontSize: '16px',
						fontWeight: 'bold',
					},
				}, 'Custom Properties'),
				React.createElement('input', {
					type: 'text',
					placeholder: 'Filter properties...',
					value: filter,
					onChange: this.handleFilterChange('player'),
					style: {
						width: '100%',
						padding: '8px 12px',
						borderRadius: '4px',
						fontFamily: 'var(--font-game-text)',
						fontSize: '14px',
						background: 'var(--main-bg-color)',
						color: 'var(--main-text-color)',
						outline: 'none',
					},
				}),
				filteredProperties.length > 0
					? (PaginatedTable ? React.createElement(PaginatedTable, {
						key: 'player-properties-table',
						items: filteredProperties,
						renderRow: renderRow,
						headers: tableHeaders,
						colgroup: colgroup,
						minItemsPerPage: 5,
						rowHeight: 70,
						maxHeight: '100%',
						className: 'player-properties-table',
						currentPage: this.getPage('player'),
						onPageChange: (page: number) => this.setPage('player', page),
					}) : React.createElement('table', { style: { width: '100%', borderCollapse: 'collapse' } },
						colgroup,
						tableHeaders,
						React.createElement('tbody', null,
							filteredProperties.map((property: CustomProperty) => renderRow(property, 0)),
						),
					))
					: React.createElement('div', { style: { marginTop: '8px' } }, 'No Player Properties match filter conditions.'),
			),
		);
	}

	openPlayerEditDialog(): void {
		const dialogRoot = document.getElementById('player-edit-dialog-root');
		if (!dialogRoot) return;

		const ReactDOM = (window as any).ReactDOM;
		const PlayerEditDialog = (window as any).PlayerEditDialog;
		if (!ReactDOM || !PlayerEditDialog) return;

		const handleSave = (name: string, gender: string) => {
			if (TheGame && TheGame.Player) {
				TheGame.Player.Name = name;
				TheGame.Player.PlayerName = name;
				TheGame.Player.Gender = gender;
				TheGame.Player.PlayerGender = gender;
			}

			// Close dialog
			if (ReactDOM.createRoot) {
				const root = (window as any).playerEditDialogRoot;
				if (root) root.render(null);
			} else {
				ReactDOM.render(null, dialogRoot);
			}

			// Force refresh of current view
			this.setState({ refreshKey: this.state.refreshKey + 1 });
		};

		const handleCancel = () => {
			if (ReactDOM.createRoot) {
				const root = (window as any).playerEditDialogRoot;
				if (root) root.render(null);
			} else {
				ReactDOM.render(null, dialogRoot);
			}
		};

		if (ReactDOM.createRoot) {
			let root = (window as any).playerEditDialogRoot;
			if (!root) {
				root = ReactDOM.createRoot(dialogRoot);
				(window as any).playerEditDialogRoot = root;
			}
			root.render(React.createElement(PlayerEditDialog, {
				onSave: handleSave,
				onCancel: handleCancel,
			}));
		} else {
			ReactDOM.render(
				React.createElement(PlayerEditDialog, {
					onSave: handleSave,
					onCancel: handleCancel,
				}),
				dialogRoot,
			);
		}
	}

	openPlayerPropertyEditDialog(property: CustomProperty): void {
		const dialogRoot = document.getElementById('property-edit-dialog-root');
		if (!dialogRoot) return;

		const ReactDOM = (window as any).ReactDOM;
		const PropertyEditDialog = (window as any).PropertyEditDialog;
		if (!ReactDOM || !PropertyEditDialog) return;

		// Initialize cheatFreezes.customProperties if needed
		if (!cheatFreezes.customProperties) {
			(cheatFreezes as any).customProperties = {};
		}
		const customProps = cheatFreezes.customProperties!;
		if (!customProps['player']) {
			customProps['player'] = {};
		}

		const handleSave = (value: string, frozen: boolean) => {
			// Update property value
			property.Value = value;

			// Update freeze state
			if (frozen) {
				if (!cheatFreezes.customProperties) {
					(cheatFreezes as any).customProperties = {};
				}
				const props = cheatFreezes.customProperties!;
				if (!props['player']) {
					props['player'] = {};
				}
				props['player'][property.Name] = true;
			} else {
				if (cheatFreezes.customProperties && cheatFreezes.customProperties['player']) {
					delete cheatFreezes.customProperties['player'][property.Name];
				}
			}

			// Close the edit dialog only
			if (ReactDOM.createRoot) {
				const root = (window as any).propertyEditDialogRoot;
				if (root) root.render(null);
			} else {
				ReactDOM.render(null, dialogRoot);
			}

			// Force refresh of the player tab
			this.setState({ refreshKey: this.state.refreshKey + 1 });
		};

		const handleCancel = () => {
			if (ReactDOM.createRoot) {
				const root = (window as any).propertyEditDialogRoot;
				if (root) root.render(null);
			} else {
				ReactDOM.render(null, dialogRoot);
			}
		};

		if (ReactDOM.createRoot) {
			let root = (window as any).propertyEditDialogRoot;
			if (!root) {
				root = ReactDOM.createRoot(dialogRoot);
				(window as any).propertyEditDialogRoot = root;
			}
			root.render(React.createElement(PropertyEditDialog, {
				entityId: 'player',
				property: property,
				onSave: handleSave,
				onCancel: handleCancel,
			}));
		} else {
			ReactDOM.render(
				React.createElement(PropertyEditDialog, {
					entityId: 'player',
					property: property,
					onSave: handleSave,
					onCancel: handleCancel,
				}),
				dialogRoot,
			);
		}
	}
}

(window as any).CheatMenuTabs = CheatMenuTabs;
export default CheatMenuTabs;
